/**
 * Название юрлица на экране.
 *
 * Форма и название хранятся ОТДЕЛЬНО («ООО» + «32 Белых»), потому что в
 * документах они склеиваются по-разному. На экранах их собирали вручную
 * выражением `shortForm ? shortForm + ' «' + name + '»' : name`, и оно давало
 * мусор на незаполненных записях:
 *
 *   - новое юрлицо (обе части пустые) — ПУСТОЙ заголовок, будто название
 *     потерялось, хотя его просто ещё не ввели;
 *   - заполнена только форма — «ООО «»», кавычки вокруг пустоты.
 *
 * Здесь оба случая названы явно, а `placeholder` говорит вызывающему, что
 * показывать текст надо приглушённым: это подсказка, а не название.
 */
import { formatDateLong } from './dates.js';
/**
 * Форма и название вместе: «ООО «32 Белых»».
 *
 * Если название УЖЕ несёт кавычки, снаружи их не добавляем. У «студии красоты
 * и здоровья «Улыбка»» кавычки стоят внутри официального названия, и общее
 * правило дало бы «ООО «студия красоты и здоровья «Улыбка»»» — двойные
 * кавычки, каких нет ни в одном документе лаборатории.
 *
 * Пустая форма — не выдумка: у клиник, платящих наличными, юрлицо заводится
 * ради связи с врачом и нередко остаётся без формы. Без проверки выходило
 * « «Улыбка»» — пробел впереди и кавычки, которых в названии нет. Оба
 * вызывающих носили эту проверку у себя; её место — в самой функции.
 */
export function withForm(form, name) {
    const short = form.trim();
    if (!short)
        return name;
    return /[«»"]/.test(name) ? `${short} ${name}` : `${short} «${name}»`;
}
export function legalTitle(legal) {
    const name = legal.name.trim();
    const form = legal.shortForm.trim();
    if (name && form)
        return { text: withForm(form, name), placeholder: false };
    if (name)
        return { text: name, placeholder: false };
    // Одна форма без названия — уже что-то: «ИП» лучше, чем пустота.
    if (form)
        return { text: form, placeholder: false };
    return { text: 'Юрлицо без названия', placeholder: true };
}
/** Только текст — там, где приглушать нечего (заголовок окна, подпись кнопки). */
export function legalTitleText(legal) {
    return legalTitle(legal).text;
}
/**
 * Строка договора для интерфейса: «№8 от 12 февраля 2024».
 *
 * Дата — СЛОВОМ, как набраны все тридцать пять заведённых договоров и как
 * написано на самой бумаге. В счёте и акте основание собирается отдельно и
 * пишет дату точками — так принято в реквизитах; здесь же строку читают
 * глазами, сверяя с папкой договоров.
 *
 * Собирается ЗДЕСЬ, а не в форме. Раньше её склеивала карточка клиники, и
 * стоило поправить номер из другого места — из вкладки «Договоры», из ответа
 * API, — как номер менялся, а строка оставалась прежней: в счёте печаталось
 * основание со старым номером.
 *
 * Без номера строки нет вовсе: «№ от 12.03.2025» — не основание, а мусор.
 */
export function contractText(contractNo, contractDate) {
    const номер = (contractNo ?? '').trim();
    if (!номер)
        return '';
    const дата = contractDate ? formatDateLong(contractDate) : '';
    return дата ? `№${номер} от ${дата}` : `№${номер}`;
}
//# sourceMappingURL=legal-name.js.map